import { BetaTag } from '@client/components/_ui/display/BetaTag';
import { cn } from '@client/utils/styling/cn';
import { Loader2 } from 'lucide-react';
import { cloneElement, forwardRef, isValidElement, useMemo } from 'react';
import type { ButtonProps } from './types';

const sizeClasses = {
    micro: 'btn-micro',
    small: 'btn-sm',
    middle: 'btn-md',
    large: 'btn-lg',
} as const;

const typeClasses = {
    primary: 'btn-modern-primary',
    secondary: 'btn-modern-secondary',
    tertiary: 'btn-modern-tertiary',
    dashed: 'btn-modern-dashed',
    danger: 'btn-modern-danger',
    'danger-filled': 'btn-modern-danger-filled',
    link: 'btn-modern-link',
} as const;

const iconSizes = {
    micro: 12,
    small: 14,
    middle: 16,
    large: 20,
} as const;

/**
 * Modern Button component with HTML <button> base
 *
 * Uses the Modern Minimal design system for styling.
 * Icons are automatically sized based on the button size (micro: 12px, small: 14px, middle: 16px, large: 20px).
 *
 * @example
 * // Primary button with icon (size auto-applied)
 * <Button type="primary" icon={<Plus />}>
 *   Create New
 * </Button>
 *
 * @example
 * // Submit button for external form
 * <Button type="primary" htmlType="submit" form="my-form" loading={isPending}>
 *   Save Changes
 * </Button>
 *
 * @example
 * // Icon-only button (auto-detected, size auto-applied)
 * <Button type="secondary" icon={<Settings />} />
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            type = 'secondary',
            size = 'middle',
            icon,
            iconPosition = 'start',
            loading = false,
            htmlType = 'button',
            form,
            iconOnly,
            block = false,
            disabled,
            className,
            children,
            badge,
            beta,
            danger,
            color,
            ...props
        },
        ref,
    ) => {
        const isIconOnly = iconOnly ?? (!children && !!icon);

        const buttonClasses = useMemo(() => {
            const baseClass = isIconOnly
                ? type === 'tertiary'
                    ? 'btn-modern-icon-tertiary'
                    : type === 'secondary'
                      ? 'btn-modern-secondary'
                      : 'btn-modern-icon'
                : typeClasses[type];

            // Default to 'accent' for primary buttons when no color specified
            const effectiveColor = color ?? (type === 'primary' ? 'accent' : undefined);

            return cn(
                'btn-variant',
                baseClass,
                sizeClasses[size],
                // Only text-bearing variants need the square treatment — the
                // dedicated icon classes are already square via symmetric
                // padding, and a block button must keep its full width.
                isIconOnly && !block && baseClass === 'btn-modern-secondary' && 'btn-icon-only',
                effectiveColor && `btn-color-${effectiveColor}`,
                danger && 'btn-danger',
                loading && 'loading',
                block && 'w-full',
                className,
            );
        }, [type, size, isIconOnly, color, danger, loading, block, className]);

        const renderIcon = () => {
            if (loading) {
                return <Loader2 className="btn-icon animate-spin" size={iconSizes[size]} />;
            }
            if (icon) {
                const sizedIcon = isValidElement(icon)
                    ? cloneElement(icon as React.ReactElement<{ size?: number }>, { size: iconSizes[size] })
                    : icon;
                return <span className="btn-icon">{sizedIcon}</span>;
            }
            return null;
        };

        return (
            <button
                ref={ref}
                type={htmlType}
                form={form}
                disabled={disabled || loading}
                aria-busy={loading}
                className={buttonClasses}
                {...props}
            >
                {iconPosition === 'start' && renderIcon()}
                {children}
                {beta && <BetaTag />}
                {badge}
                {iconPosition === 'end' && renderIcon()}
            </button>
        );
    },
);

Button.displayName = 'Button';
